import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserFromStorage, clearUserStorage } from '../services/auth';
import RelojCuentaAtras from '../components/RelojCuentaAtras';
import PerfilSelector from '../components/PerfilSelector';
import PinModal from '../components/PinModal';
import PerfilPeticiones from './PerfilPeticiones';
import api from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [perfilActivo, setPerfilActivo] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('perfilActual'));
      return stored?.id ? stored : null;
    } catch (e) {
      console.error('Error al leer perfil de localStorage:', e);
      return null;
    }
  });
  const [perfiles, setPerfiles] = useState([]);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);
  const [mostrarRelojGrande, setMostrarRelojGrande] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const activarPerfil = (perfil) => {
    if (!perfil?.id) {
      setError('El perfil no tiene ID válido');
      return false;
    }

    try {
      const perfilCompleto = {
        id: perfil.id,
        nombre: perfil.nombre,
        es_admin: perfil.es_admin,
        avatar: perfil.avatar || 'default.png'
      };

      localStorage.setItem('perfilActual', JSON.stringify(perfilCompleto));
      localStorage.setItem('perfilActivoId', perfil.id);
      setPerfilActivo(perfilCompleto);
      setMostrarRelojGrande(true);
      return true;
    } catch (e) {
      console.error('Error al activar perfil:', e);
      setError('Error al guardar el perfil');
      return false;
    }
  };

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        setError(null);
        
        const storedUser = getUserFromStorage();
        if (!storedUser?.id) {
          navigate('/', { replace: true });
          return;
        }

        // 1. Verificar perfil desde estado de navegación
        const perfilFromState = location.state?.perfil;
        if (perfilFromState?.id) {
          if (activarPerfil(perfilFromState)) {
            setUser(storedUser);
            return;
          }
        }

        // 2. Verificar localStorage
        const storedPerfil = JSON.parse(localStorage.getItem('perfilActual'));
        if (storedPerfil?.id) {
          setPerfilActivo(storedPerfil);
          setUser(storedUser);
          return;
        }

        // 3. Cargar perfiles desde API
        setUser(storedUser);
        const res = await api.get(`/perfiles/${storedUser.id}`);
        
        if (!res.data || res.data.length === 0) {
          if (storedUser.isadmin) {
            navigate('/crear-perfil');
          } else {
            setError('No tienes perfiles asignados. Contacta al administrador.');
          }
          return;
        }

        setPerfiles(res.data);

        // Si solo hay un perfil, activarlo automáticamente
        if (res.data.length === 1) {
          activarPerfil(res.data[0]);
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError(err.response?.data?.error || err.message || 'Error al cargar el dashboard');
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, [navigate, location.state]);

  const seleccionarPerfil = (perfil) => {
    if (perfil.pin && perfil.pin.length >= 4) {
      setPerfilSeleccionado(perfil);
    } else {
      activarPerfil(perfil);
    }
  };

  const confirmarPinCorrecto = (perfil) => {
    activarPerfil(perfil);
    setPerfilSeleccionado(null);
  };

  const cancelarPin = () => {
    setPerfilSeleccionado(null);
  };

  const cerrarSesion = () => {
    clearUserStorage();
    localStorage.removeItem('perfilActual');
    localStorage.removeItem('perfilActivoId');
    navigate('/', { replace: true });
  };

  const salirDelPerfil = () => {
    localStorage.removeItem('perfilActual');
    setPerfilActivo(null);
    setMostrarRelojGrande(false);
    navigate('/seleccionar-perfil', { replace: true });
  };

  const crearPerfilNuevo = () => {
    navigate('/crear-perfil');
  };

  const recargarDashboard = () => {
    window.location.reload();
  };

  if (cargando) {
    return (
      <div className="cargando-container">
        <div className="cargando-spinner"></div>
        <p>Cargando dashboard...</p>
        {!perfilActivo && <p>Buscando perfil activo...</p>}
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={recargarDashboard} className="btn-reintentar">
            Recargar
          </button>
          <button onClick={cerrarSesion} className="btn-cerrar-sesion">
            Volver al inicio
          </button>
          {user?.isadmin && (
            <button onClick={crearPerfilNuevo} className="btn-crear-perfil">
              Crear perfil
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Bienvenido, {user.username}</h1>
          {perfilActivo && (
            <div className="perfil-info">
              <div className="perfil-avatar">
                <img 
                  src={`/avatars/${perfilActivo.avatar}`} 
                  alt={`Avatar ${perfilActivo.nombre}`}
                  onError={(e) => {
                    e.target.src = '/avatars/default.png';
                  }}
                />
                <span>{perfilActivo.nombre}</span>
              </div>
              {perfilActivo.es_admin && (
                <button onClick={crearPerfilNuevo} className="btn-crear-perfil">
                  + Crear perfil
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {mostrarRelojGrande && perfilActivo && (
        <div className="reloj-grande-overlay">
          <div className="reloj-grande-content">
          <RelojCuentaAtras fechaExp={new Date(user.fechaExp)} grande />
            <button 
              onClick={() => setMostrarRelojGrande(false)}
              className="btn-cerrar-reloj"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <main className="dashboard-main">
        {!perfilActivo ? (
          <div className="seleccion-perfil">
            <h2>Selecciona un perfil</h2>
            {perfiles.length > 0 ? (
              <>
                <PerfilSelector 
                  perfiles={perfiles} 
                  seleccionar={seleccionarPerfil} 
                />
                <button 
                  onClick={salirDelPerfil} 
                  className="btn-volver"
                >
                  Volver
                </button>
              </>
            ) : (
              <div className="sin-perfiles">
                <p>No hay perfiles disponibles</p>
                {user.isadmin && (
                  <button onClick={crearPerfilNuevo} className="btn-crear-perfil">
                    Crear primer perfil
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="contenido-perfil">
            {!mostrarRelojGrande && (
              <div className="reloj-pequeno">
                <RelojCuentaAtras fechaExp={new Date(user.fechaExp)} />
              </div>
            )}

            <section className="seccion-peticiones">
              <h2>Mis Peticiones</h2>
              {perfilActivo?.id ? (
                <PerfilPeticiones 
                  perfilId={perfilActivo.id} 
                  key={perfilActivo.id}
                />
              ) : (
                <div className="error-perfil">
                  <p>No se ha seleccionado un perfil válido</p>
                  <div className="error-actions">
                    <button onClick={salirDelPerfil}>Seleccionar perfil</button>
                    <button onClick={recargarDashboard}>Recargar</button>
                  </div>
                </div>
              )}
            </section>

            <div className="acciones-perfil">
              <button onClick={salirDelPerfil} className="btn-salir-perfil">
                Cambiar de perfil
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="dashboard-footer">
        <button onClick={cerrarSesion} className="btn-cerrar-sesion">
          Cerrar sesión
        </button>
      </footer>

      {perfilSeleccionado && (
        <PinModal
          perfil={perfilSeleccionado}
          onConfirm={confirmarPinCorrecto}
          onCancel={cancelarPin}
        />
      )}
    </div>
  );
}

export default Dashboard;